/**
 * Hook that owns the Meter Allocation Config editable table's local row
 * state, validation (Save-only), and serialization to
 * organization.options.meter_allocation_config.
 *
 * The persisted row shape is the bare array consumed by the allocation meter
 * worker: { name, item_sub_type_code, attribute_type_code,
 * well_test_attribute_type_code }.
 */
import { useCallback, useRef, useState } from 'react';
import { parseAttributeTypeCodes } from './useWellTestOverviewColumns';
const WHOLE_NUMBER_PATTERN = /^\d+$/;
function parseWholeNumber(text) {
    const trimmed = text.trim();
    if (!WHOLE_NUMBER_PATTERN.test(trimmed)) {
        return null;
    }
    return parseInt(trimmed, 10);
}
function storedRows(org) {
    var _a;
    const stored = (_a = org === null || org === void 0 ? void 0 : org.options) === null || _a === void 0 ? void 0 : _a.meter_allocation_config;
    return Array.isArray(stored) ? stored : undefined;
}
function toRow(row) {
    var _a;
    return {
        id: crypto.randomUUID(),
        name: (_a = row.name) !== null && _a !== void 0 ? _a : '',
        itemSubTypeCodeText: typeof row.item_sub_type_code === 'number' ? String(row.item_sub_type_code) : '',
        attributeTypeCodeText: (row.attribute_type_code || []).join(', '),
        wellTestAttributeTypeCodeText: typeof row.well_test_attribute_type_code === 'number' ? String(row.well_test_attribute_type_code) : '',
    };
}
function toStoredRow(row) {
    var _a, _b, _c;
    return {
        name: row.name.trim(),
        item_sub_type_code: (_a = parseWholeNumber(row.itemSubTypeCodeText)) !== null && _a !== void 0 ? _a : 0,
        attribute_type_code: (_b = parseAttributeTypeCodes(row.attributeTypeCodeText)) !== null && _b !== void 0 ? _b : [],
        well_test_attribute_type_code: (_c = parseWholeNumber(row.wellTestAttributeTypeCodeText)) !== null && _c !== void 0 ? _c : 0,
    };
}
export function useMeterAllocationConfig(org) {
    const initialStoredRows = storedRows(org);
    const hadStoredOptionRef = useRef(initialStoredRows != null);
    const [rows, setRows] = useState(() => (initialStoredRows !== null && initialStoredRows !== void 0 ? initialStoredRows : []).map(toRow));
    const [errors, setErrors] = useState({});
    const reset = useCallback(() => {
        const stored = storedRows(org);
        hadStoredOptionRef.current = stored != null;
        setRows((stored !== null && stored !== void 0 ? stored : []).map(toRow));
        setErrors({});
    }, [org]);
    const addRow = useCallback(() => {
        setRows((previous) => [...previous, {
                id: crypto.randomUUID(),
                name: '',
                itemSubTypeCodeText: '',
                attributeTypeCodeText: '',
                wellTestAttributeTypeCodeText: '',
            }]);
    }, []);
    const removeRow = useCallback((id) => {
        setRows((previous) => previous.filter((row) => row.id !== id));
    }, []);
    const updateRow = useCallback((id, field, value) => {
        setRows((previous) => previous.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    }, []);
    const moveRow = useCallback((fromIndex, toIndex) => {
        setRows((previous) => {
            if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= previous.length || toIndex >= previous.length) {
                return previous;
            }
            const next = [...previous];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return next;
        });
    }, []);
    const validate = useCallback(() => {
        const next = {};
        for (const row of rows) {
            const rowErrors = {};
            if (!row.name.trim()) {
                rowErrors.name = 'Required';
            }
            if (!row.itemSubTypeCodeText.trim()) {
                rowErrors.itemSubTypeCode = 'Required';
            }
            else if (parseWholeNumber(row.itemSubTypeCodeText) === null) {
                rowErrors.itemSubTypeCode = 'Must be a whole number.';
            }
            if (!row.attributeTypeCodeText.trim()) {
                rowErrors.attributeTypeCode = 'Required';
            }
            else if (parseAttributeTypeCodes(row.attributeTypeCodeText) === null) {
                rowErrors.attributeTypeCode = 'Enter digits separated by commas (e.g. 78,206 or 79, 201).';
            }
            if (!row.wellTestAttributeTypeCodeText.trim()) {
                rowErrors.wellTestAttributeTypeCode = 'Required';
            }
            else if (parseWholeNumber(row.wellTestAttributeTypeCodeText) === null) {
                rowErrors.wellTestAttributeTypeCode = 'Must be a whole number.';
            }
            if (Object.keys(rowErrors).length > 0) {
                next[row.id] = rowErrors;
            }
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    }, [rows]);
    const toOptionsPatch = useCallback(() => {
        if (rows.length === 0 && !hadStoredOptionRef.current) {
            return {};
        }
        return { meter_allocation_config: rows.map(toStoredRow) };
    }, [rows]);
    return { rows, errors, addRow, removeRow, updateRow, moveRow, validate, reset, toOptionsPatch };
}
